import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import {
    Alert,
    AlertTitle,
    FormControl,
    FormHelperText,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { PageTitle } from '../../../_metronic/layout/core';
import styleScss from './styles.module.scss';
import { LinearLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import BackButton from '../../components/BackButton/BackButton';
import { getRespuestas, getVRD, postPonderar, postRespuestas } from '../../modules/apps/user-management/users-list/core/_requests';
import parameterSingleton from '../../services/Parameters.service';
import parameters from '../../util/parameters';
import { ParameterConfigMmodel } from '../../models/parameter.model';

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const COLORES: Record<string, { background: string; color: string }> = {
    rojo: { background: '#F7DDDE', color: '#A3696F' },
    naranja: { background: '#FFE5C4', color: '#A68550' },
    verde: { background: '#D4EDDA', color: '#155724' },
};

const ESTILO_HEADER = { fontFamily: 'Poppins', fontSize: '16px', padding: '8px' };
const ESTILO_HEADER_RESULTADO = { fontFamily: 'Poppins', fontSize: '16.25px' };
const ESTILO_CELDA = { fontFamily: 'Poppins', fontSize: '14.5px' };
const ESTILO_TABLA_CONTAINER = { overflowY: 'visible', overflowWrap: 'break-word' } as const;

const MSG_ERROR_GENERICO = 'Ocurrio un error procesando la solicitud.';

// ---------------------------------------------------------------------------
// Helpers puros
// ---------------------------------------------------------------------------

const toArray = (valor: any) => (Array.isArray(valor) ? valor : [valor]);

const sortByOrden = (a: any, b: any) =>
    String(a?.orden ?? '').localeCompare(String(b?.orden ?? ''), undefined, { numeric: true });

/** Aplana los bloques de una sección y devuelve sus preguntas (aspectos). */
const aspectosDeSeccion = (seccion: any): any[] =>
    seccion?.Preguntas?.flatMap((bloque: any) => bloque?.Preguntas ?? []) ?? [];

const buscarRespuesta = (respuestas: any[] | undefined, idPregunta: any) =>
    respuestas?.find((r: any) => r.id_pregunta == idPregunta);

const esRespuestaVacia = (respuesta: any) =>
    !respuesta ||
    respuesta.codigo_respuesta === undefined ||
    respuesta.codigo_respuesta === null ||
    respuesta.codigo_respuesta === '' ||
    (Array.isArray(respuesta.codigo_respuesta) && respuesta.codigo_respuesta.length === 0);

const obtenerError = (aspecto: any, errores: any[]) =>
    errores?.find((error: any) => error.id_pregunta === aspecto.id)?.error ?? '';

const contieneValor = (codigoRespuestaEsperada: any, valorBuscado: any) => {
    if (
        codigoRespuestaEsperada === undefined ||
        codigoRespuestaEsperada === null ||
        valorBuscado === undefined ||
        valorBuscado === null ||
        valorBuscado === ''
    ) {
        return false;
    }
    return String(codigoRespuestaEsperada).split('|').includes(String(valorBuscado));
};

/** Indica si una pregunta debe mostrarse según su precondición "mostrar". */
const cumplePrecondicionMostrar = (aspecto: any, respuestas: any[]) => {
    const mostrar = aspecto?.estructura?.precondicion?.mostrar;
    if (!mostrar) return true;

    return toArray(mostrar).every((regla: any) =>
        contieneValor(regla.codigo_respuesta, buscarRespuesta(respuestas, regla.codigo_pregunta)?.codigo_respuesta)
    );
};

/** Elimina (en cascada) las respuestas de las preguntas que dependen de `idPadre`. */
const limpiarRespuestasHijas = (secciones: any[], idPadre: number | string, respuestas: any[]) => {
    const todasPreguntas = secciones?.flatMap(aspectosDeSeccion) ?? [];
    const idsHijas = new Set<number>();
    const cola = [Number(idPadre)];

    while (cola.length > 0) {
        const padreId = cola.shift();
        todasPreguntas.forEach((p: any) => {
            const mostrar = p.estructura?.precondicion?.mostrar;
            if (!mostrar || idsHijas.has(p.id)) return;
            if (toArray(mostrar).some((r: any) => r.codigo_pregunta == padreId)) {
                idsHijas.add(p.id);
                cola.push(p.id);
            }
        });
    }

    return idsHijas.size ? respuestas.filter((r: any) => !idsHijas.has(r.id_pregunta)) : respuestas;
};

/** Marca automáticamente otras preguntas según la precondición "marcar". */
const aplicarMarcado = (aspecto: any, idSeccionActual: number, valorSeleccionado: any, respuestas: any[]) => {
    const marcar = aspecto.estructura?.precondicion?.marcar;
    if (!marcar) return respuestas;

    let nuevaRespuesta = respuestas;

    toArray(marcar).forEach((regla: any) => {
        const cumple = Array.isArray(regla.codigo_respuesta)
            ? regla.codigo_respuesta.includes(valorSeleccionado)
            : regla.codigo_respuesta === valorSeleccionado;
        if (!cumple) return;

        const codigoSeccion = regla?.codigo_seccion || idSeccionActual;
        const codigoMarcar = regla?.codigo_marcar ?? 1; // "Sí" = 1

        String(regla.codigo_pregunta).split('|').forEach((idStr: string) => {
            const idDestino = Number(idStr);
            nuevaRespuesta = buscarRespuesta(nuevaRespuesta, idDestino)
                ? nuevaRespuesta.map((r: any) => (r.id_pregunta == idDestino ? { ...r, codigo_respuesta: codigoMarcar } : r))
                : [...nuevaRespuesta, { id_pregunta: idDestino, id_seccion: codigoSeccion, codigo_respuesta: codigoMarcar }];
        });
    });

    return nuevaRespuesta;
};

/** Reemplaza las opciones de las preguntas parametrizadas con PAIS por la lista de países. */
const inyectarPaises = (evaluacion: any, paises: ParameterConfigMmodel[]) => {
    const paisesUnicos = Array.from(new Map(paises.map((p) => [p.code, p])).values());
    const opcionesPais = paisesUnicos.map((p) => ({ codigo: p.code, value: p.value }));

    return {
        ...evaluacion,
        Secciones: evaluacion.Secciones.map((seccion: any) => ({
            ...seccion,
            Preguntas: seccion.Preguntas.map((bloque: any) => ({
                ...bloque,
                Preguntas: bloque.Preguntas.map((aspecto: any) =>
                    aspecto.estructura?.parameters === parameters.PAIS
                        ? { ...aspecto, estructura: { ...aspecto.estructura, option: opcionesPais } }
                        : aspecto
                ),
            })),
        })),
    };
};

const logError = (error: any) => {
    console.error(error?.response ? error.response.data.error : MSG_ERROR_GENERICO);
};

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

type SeccionPreguntasProps = {
    seccion: any;
    respuestas: any[];
    errores: any[];
    onChange: (aspecto: any, idSeccion: number, value: any) => void;
};

const SeccionPreguntas = ({ seccion, respuestas, errores, onChange }: SeccionPreguntasProps) => {
    const aspectosVisibles = aspectosDeSeccion(seccion)
        .sort(sortByOrden)
        .filter((aspecto) => cumplePrecondicionMostrar(aspecto, respuestas));

    return (
        <div>
            <h2 style={{ padding: '16px' }}>{seccion.titulo}</h2>
            <p style={{ paddingLeft: 16, paddingRight: 16 }}>{seccion.descripcion}</p>

            <TableContainer component={Paper} sx={{ marginBottom: 5 }} style={ESTILO_TABLA_CONTAINER}>
                <Table style={{ overflowY: 'visible' }}>
                    <TableHead className={styleScss['th-custom']}>
                        <TableRow>
                            <TableCell style={ESTILO_HEADER}><h4>Preguntas</h4></TableCell>
                            <TableCell style={ESTILO_HEADER}><h4>Respuestas</h4></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: seccion?.color ?? '' }}>
                        {aspectosVisibles.map((aspecto: any) => (
                            <FilaPregunta
                                key={aspecto.id}
                                aspecto={aspecto}
                                seccion={seccion}
                                respuestas={respuestas}
                                error={obtenerError(aspecto, errores)}
                                onChange={onChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

type FilaPreguntaProps = {
    aspecto: any;
    seccion: any;
    respuestas: any[];
    error: string;
    onChange: (aspecto: any, idSeccion: number, value: any) => void;
};

const FilaPregunta = ({ aspecto, seccion, respuestas, error, onChange }: FilaPreguntaProps) => {
    const tipo = aspecto.estructura?.tipo;
    const opciones = aspecto.estructura?.option ?? [];
    const claseBorde = aspecto.estructura?.borde != null
        ? `${styleScss['bordeado']} ${styleScss[`borde-${aspecto.estructura.borde}`]} ${styleScss[`seccion-${seccion.id}`]}`
        : '';

    return (
        <TableRow className={claseBorde}>
            <TableCell style={ESTILO_CELDA}>
                {tipo === 'label' ? (
                    <label style={ESTILO_CELDA}>
                        <b>{aspecto.valor ?? aspecto.titulo}</b>
                    </label>
                ) : (
                    aspecto.titulo
                )}
            </TableCell>

            <TableCell style={{ padding: '8px 8px' }}>
                {tipo === 'select' && (
                    <FormControl error={!!error} fullWidth>
                        <Select
                            size='small'
                            sx={{ backgroundColor: '#fff' }}
                            value={buscarRespuesta(respuestas, aspecto.id)?.codigo_respuesta || ''}
                            onChange={(e) => onChange(aspecto, seccion.id, e.target.value)}
                        >
                            {opciones.map((op: any) => (
                                <MenuItem key={op.codigo} value={op.codigo}>{op.value}</MenuItem>
                            ))}
                        </Select>
                        {error && <FormHelperText>{error}</FormHelperText>}
                    </FormControl>
                )}

                {tipo === 'option' && (
                    <FormControl error={!!error} fullWidth>
                        {opciones.map((op: any) => (
                            <option key={op.codigo} value={op.codigo}>{op.value}</option>
                        ))}
                        {error && <FormHelperText>{error}</FormHelperText>}
                    </FormControl>
                )}
            </TableCell>
        </TableRow>
    );
};

const COLOR_ENLACE = '#2266CC';

/** Muestra un texto convirtiendo sus URL en enlaces. split() con grupo de captura deja las URL en las posiciones impares. */
const TextoConEnlaces = ({ texto }: { texto: string }) => (
    <>
        {texto.split(/(https?:\/\/\S+)/g).map((parte, i) =>
            i % 2 === 1 ? (
                <a key={i} href={parte} target='_blank' rel='noopener noreferrer' style={{ color: COLOR_ENLACE }}>
                    {parte}
                </a>
            ) : (
                parte
            )
        )}
    </>
);

/** Recomendación de la oferta (HU33 Tabla 4): texto y enlaces, uno por línea. */
const RecomendacionOferta = ({ oferta }: { oferta: any }) => {
    const lineas = [oferta?.recomendacion, ...(Array.isArray(oferta?.detalle) ? oferta.detalle : [])].filter(Boolean);

    return (
        <>
            {lineas.map((linea: string, i: number) => (
                <div key={i} style={{ whiteSpace: 'pre-line', wordBreak: 'break-word', marginTop: i ? 8 : 0 }}>
                    <TextoConEnlaces texto={linea} />
                </div>
            ))}
        </>
    );
};

const ResultadoPonderacion = ({ resultado }: { resultado: any }) => {
    const puntajeGlobal = resultado?.resultado?.tipo_puntaje?.puntaje_global;
    const colorCategoria = COLORES[puntajeGlobal?.categoria?.color];

    return (
        <div>
            <TableContainer component={Paper} sx={{ marginBottom: 5 }} style={ESTILO_TABLA_CONTAINER}>
                <Table style={{ overflowY: 'visible' }}>
                    <TableHead className={styleScss['th-custom']}>
                        <TableRow>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Tipo de puntaje</h4></TableCell>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Descripción</h4></TableCell>
                            <TableCell style={{ ...ESTILO_HEADER_RESULTADO, width: 0 }}><h4>Puntaje</h4></TableCell>
                            <TableCell style={{ ...ESTILO_HEADER_RESULTADO, width: 0 }}><h4>Categoría</h4></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell style={{ ...ESTILO_CELDA, fontWeight: 'bold' }}>Puntaje global</TableCell>
                            <TableCell style={ESTILO_CELDA}>{puntajeGlobal?.descripcion}</TableCell>
                            <TableCell style={{ ...ESTILO_CELDA, textAlign: 'center' }}>{puntajeGlobal?.puntaje ?? 0}%</TableCell>
                            <TableCell style={{ ...ESTILO_CELDA, textAlign: 'center' }}>
                                <div style={{ padding: 10, borderRadius: 5, background: colorCategoria?.background || '#FFFFFF' }}>
                                    <span style={{ color: colorCategoria?.color || '#000000' }}>
                                        {puntajeGlobal?.categoria?.categoria}
                                    </span>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ marginBottom: 5 }} style={ESTILO_TABLA_CONTAINER}>
                <Table style={{ overflowY: 'visible' }}>
                    <TableHead className={styleScss['th-custom']}>
                        <TableRow>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Oferta</h4></TableCell>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Descripción</h4></TableCell>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Recomendación</h4></TableCell>
                            <TableCell style={ESTILO_HEADER_RESULTADO}><h4>Se recomienda</h4></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultado?.resultado?.ofertas?.map((oferta: any, index: number) => (
                            <TableRow key={index}>
                                <TableCell style={{ ...ESTILO_CELDA, fontWeight: 'bold' }}>{oferta.titulo}</TableCell>
                                <TableCell style={ESTILO_CELDA}>{oferta.descripcion}</TableCell>
                                <TableCell style={ESTILO_CELDA}><RecomendacionOferta oferta={oferta} /></TableCell>
                                <TableCell style={ESTILO_CELDA}>{oferta.se_recomienda}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

type SnackbarState = { open: boolean; exito: boolean; mensaje: string };

const ListRegisterCounselors = () => {
    const { id } = useParams();
    const [data, setData] = useState<any>({});
    const [errores, setErrores] = useState<any[]>([]);
    const [evaluacionData, setEvaluacionData] = useState<any>({ respuesta: [] });
    const [loading, setLoading] = useState(true);
    const [restriccion, setRestriccion] = useState('');
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, exito: true, mensaje: '' });
    const listPaisRef = useRef<ParameterConfigMmodel[]>([]);

    const secciones: any[] = data?.evaluacion?.Secciones ?? [];
    const codigoDireccionamiento = data?.Direccionamiento?.codigo_direccionamiento;

    const notificar = (exito: boolean, mensaje: string, duracionMs: number) => {
        setSnackbar({ open: true, exito, mensaje });
        setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), duracionMs);
    };

    const cerrarSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const fetchPaisParams = async () => {
        try {
            listPaisRef.current = (await parameterSingleton.fetchData(parameters.PAIS)) ?? [];
        } catch (err) {
            console.error('Error trayendo países:', err);
            listPaisRef.current = [];
        }
    };

    const cargarRespuestas = async (idEvaluacion: any, idUser: any, codigoDir: any) => {
        const { data: resp } = await getRespuestas(idEvaluacion, idUser, codigoDir);
        setEvaluacionData((prev: any) => ({
            ...prev,
            respuesta: resp.respuesta,
            id_evaluacion: resp.id_evaluacion,
            id_user: resp.id_user,
            resultado: resp.resultado,
        }));
    };

    const cargarEvaluacion = async () => {
        setLoading(true);
        try {
            const { data: vrd } = await getVRD(id);

            setData({ ...vrd, evaluacion: inyectarPaises(vrd.evaluacion, listPaisRef.current) });
            setEvaluacionData((prev: any) => ({
                ...prev,
                id_user: vrd.aspirante,
                id_evaluacion: vrd.evaluacion_id,
            }));

            try {
                await cargarRespuestas(vrd.evaluacion_id, vrd.aspirante, vrd?.Direccionamiento?.codigo_direccionamiento);
            } catch (error) {
                logError(error);
            }
        } catch (error: any) {
            if (error?.response?.status === 409) setRestriccion(error.response.data.error);
            logError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            await fetchPaisParams(); // llena listPaisRef.current ANTES de cargar la evaluación
            await cargarEvaluacion();
        })();
    }, []);

    const handleChange = (aspecto: any, idSeccion: number, value: any) => {
        setEvaluacionData((prev: any) => {
            const respuestas = prev?.respuesta || [];
            const nueva = respuestas.some((r: any) => r.id_pregunta === aspecto.id)
                ? respuestas.map((r: any) => (r.id_pregunta === aspecto.id ? { ...r, codigo_respuesta: value } : r))
                : [...respuestas, { id_pregunta: aspecto.id, id_seccion: idSeccion, codigo_pregunta: aspecto.codigo, codigo_respuesta: value }];

            return {
                ...prev,
                respuesta: aplicarMarcado(aspecto, idSeccion, value, limpiarRespuestasHijas(secciones, aspecto.id, nueva)),
            };
        });
    };

    const validarRespuestasObligatorias = () => {
        const nuevosErrores = secciones
            .flatMap(aspectosDeSeccion)
            .filter((aspecto: any) =>
                aspecto.estructura?.tipo !== 'label' &&
                cumplePrecondicionMostrar(aspecto, evaluacionData.respuesta) &&
                esRespuestaVacia(evaluacionData?.respuesta?.find((r: any) => r.id_pregunta === aspecto.id))
            )
            .map((aspecto: any) => ({ id_pregunta: aspecto.id, error: 'Este campo es obligatorio' }));

        setErrores(nuevosErrores);
        return nuevosErrores.length === 0;
    };

    const enviarRespuestas = async () => {
        if (!validarRespuestasObligatorias()) {
            notificar(false, 'Debe responder todas las preguntas obligatorias.', 2500);
            return;
        }

        try {
            await postRespuestas(evaluacionData, evaluacionData.id_evaluacion, evaluacionData.id_user, codigoDireccionamiento);
            notificar(true, 'Respuestas Enviadas con éxito!', 5000);
        } catch (error: any) {
            if (error.response) {
                setErrores(error.response.data.error);
                notificar(false, 'Valide las preguntas', 1500);
            } else {
                console.error(MSG_ERROR_GENERICO);
            }
        }
    };

    const handlePonderar = async () => {
        try {
            await postPonderar(evaluacionData.id_evaluacion, evaluacionData.id_user, codigoDireccionamiento);
            await cargarRespuestas(evaluacionData.id_evaluacion, evaluacionData.id_user, codigoDireccionamiento);
            notificar(true, 'Ponderación procesada con éxito!', 4000);
        } catch (error: any) {
            console.error('Error capturado:', error);
            if (error.response) {
                notificar(false, error.response.data?.error || 'Ocurrió un error inesperado', 2500);
            } else {
                console.error(MSG_ERROR_GENERICO);
            }
        }
    };

    return (
        <>
            <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
                <div className='row mb-4' style={{ alignItems: 'center' }}>
                    <div className='col-sm-12'>
                        <div className='input-group d-flex' style={{ width: '100%' }}>
                            <div style={{ padding: '10px 0px 0px 15px' }}>
                                <div className='d-flex justify-content-start' style={{ display: 'contents', justifyContent: 'center', alignItems: 'center' }}>
                                    <BackButton />
                                    <p style={{ marginLeft: 20 }}>
                                        Esta herramienta tiene como finalidad identificar la presencia de barreras para acceder a empleo presentes en el buscador, el profesional debe seleccionar la opción que mejor describa a los buscadores de empleo.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <LinearLoadingSpinner isLoading={loading} />
                ) : restriccion ? (
                    <Alert severity='warning'>
                        <AlertTitle>No es posible registrar un nuevo VRD</AlertTitle>
                        {restriccion}
                    </Alert>
                ) : (
                    <div className={styleScss['table-limit']} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
                        <Stack padding={2}>
                            {secciones.map((seccion: any) => (
                                <SeccionPreguntas
                                    key={seccion.id}
                                    seccion={seccion}
                                    respuestas={evaluacionData.respuesta}
                                    errores={errores}
                                    onChange={handleChange}
                                />
                            ))}

                            <div className='d-flex justify-content-end' style={{ marginBottom: 50 }}>
                                <Button onClick={enviarRespuestas} variant='contained' size='large' style={{ marginRight: 10 }}>
                                    GUARDAR RESPUESTAS
                                </Button>
                                <Button variant='contained' disabled={!evaluacionData?.respuesta} size='large' onClick={handlePonderar}>
                                    PONDERAR
                                </Button>
                            </div>

                            {evaluacionData?.resultado && <ResultadoPonderacion resultado={evaluacionData.resultado} />}
                        </Stack>
                    </div>
                )}
            </div>

            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={snackbar.open} onClose={cerrarSnackbar}>
                <Alert onClose={cerrarSnackbar} variant='filled' severity={snackbar.exito ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
                    <AlertTitle>{snackbar.exito ? 'Éxito' : 'Error'}</AlertTitle>
                    {snackbar.mensaje}
                </Alert>
            </Snackbar>
        </>
    );
};

const RecordingAndWeightingVariables = () => (
    <>
        <PageTitle breadcrumbs={[]}>Registro y ponderación de variables VRD</PageTitle>
        <ListRegisterCounselors />
    </>
);

export { RecordingAndWeightingVariables };
